import { WINDOWS_TIMEZONE_MAP, DESCRIPTIVE_TIMEZONE_MAP } from './NotificationConstants';
import { formatDate } from '../../utilities/dateHelpers';

/**
 * Converts Windows timezone names to IANA timezone identifiers
 * @param windowsTimezone - Windows timezone name (e.g., "Central Standard Time" or "(UTC-06:00) Central Time (US & Canada)")
 * @returns IANA timezone identifier (e.g., "America/Chicago")
 */
export const getIANATimezone = (windowsTimezone: string): string => {
  if (WINDOWS_TIMEZONE_MAP[windowsTimezone]) {
    return WINDOWS_TIMEZONE_MAP[windowsTimezone];
  }

  const descriptiveMatch = windowsTimezone.match(/^\(UTC[+-]\d{2}:\d{2}\)\s*(.+)$/);
  if (descriptiveMatch) {
    const timezoneName = descriptiveMatch[1];
    if (DESCRIPTIVE_TIMEZONE_MAP[timezoneName]) {
      return DESCRIPTIVE_TIMEZONE_MAP[timezoneName];
    }
  }

  return windowsTimezone;
};

/**
 * Converts a timestamp from a specific timezone to local time and formats as "time ago"
 * @param timestamp - The timestamp string (e.g., "10/25/2025 10:21:08 AM")
 * @param wellTimezoneInWindowsFormat - Windows timezone format (e.g., "(UTC-06:00) Central Time (US & Canada)")
 * @returns Formatted "time ago" string (e.g., "5 mins ago", "2 hours ago")
 */
export const convertTimeFromSpecificZoneToLocalAndTimeAgo = (
  timestamp: string | null | undefined,
  wellTimezoneInWindowsFormat: string | null | undefined,
) => {
  if (!timestamp || !wellTimezoneInWindowsFormat) return '-';

  try {
    const ianaTimezone = getIANATimezone(wellTimezoneInWindowsFormat);

    const parsedDate = new Date(timestamp);
    if (isNaN(parsedDate.getTime())) return '-';

    const year = parsedDate.getFullYear();
    const month = parsedDate.getMonth() + 1;
    const day = parsedDate.getDate();
    const hours = parsedDate.getHours();
    const minutes = parsedDate.getMinutes();
    const seconds = parsedDate.getSeconds();

    // Create UTC date from components, then adjust for timezone offset
    const dateAsUTC = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
    const wellTimezoneOffsetMs = getTimezoneOffsetMs(ianaTimezone, dateAsUTC);
    const actualUTCTime = new Date(dateAsUTC.getTime() - wellTimezoneOffsetMs);

    return formatTimeAgo(actualUTCTime.toISOString());
  } catch (error) {
    console.error('Error converting timezone:', error);
    return formatTimeAgo(timestamp);
  }
};

// Calculates timezone offset in milliseconds for a specific timezone at a given date
const getTimezoneOffsetMs = (timezone: string, date: Date): number => {
  try {
    const utcTime = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);

    const timezoneTime = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);

    const utcDate = new Date(utcTime + 'Z');
    const timezoneDate = new Date(timezoneTime + 'Z');

    return timezoneDate.getTime() - utcDate.getTime();
  } catch (error) {
    console.error('Error calculating timezone offset:', error);
    return 0;
  }
};

/**
 * Formats a timestamp into a human-readable "time ago" format
 * @param timestamp - ISO timestamp string
 * @returns Formatted string (e.g., "5 mins ago", "2 hours ago", "Oct 23, 2025")
 */
export const formatTimeAgo = (timestamp: string | null | undefined) => {
  if (!timestamp) return '-';

  try {
    const now = new Date();
    const date = new Date(timestamp);

    if (isNaN(date.getTime())) return '-';

    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return `${diffSecs} sec${diffSecs !== 1 ? 's' : ''} ago`;
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays <= 3) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

    return formatDate(date);
  } catch (error) {
    return 'Invalid date';
  }
};
